/**
 * RSS Headline Fetcher — multi-feed, multi-proxy with automatic fallback.
 *
 * Strategy:
 *  - 6 different news source feeds
 *  - 3 different CORS proxies tried in order for each feed
 *  - If a proxy fails for a feed, it immediately tries the next proxy
 *  - Results from all working feed/proxy combinations are merged
 *  - As long as ANY single combination works, you get live news
 */

// ── CORS Proxies ──────────────────────────────────────────────────────────────
// Each proxy is tried in order until one works for a given feed.
const PROXIES = [
  {
    name: "corsproxy.io",
    buildUrl: (feed) => `https://corsproxy.io/?${encodeURIComponent(feed)}`,
    extractXml: async (res) => res.text(),
  },
  {
    name: "allorigins",
    buildUrl: (feed) => `https://api.allorigins.win/get?url=${encodeURIComponent(feed)}`,
    extractXml: async (res) => {
      const json = await res.json();
      return json.contents || "";
    },
  },
  {
    name: "codetabs",
    buildUrl: (feed) => `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(feed)}`,
    extractXml: async (res) => res.text(),
  },
];

// ── RSS Feed Sources ──────────────────────────────────────────────────────────
// Multiple sources so if one publication's feed is down, others fill in.
const FEEDS = [
  "https://feeds.npr.org/1001/rss.xml",                         // NPR News
  "https://feeds.bbci.co.uk/news/world/rss.xml",                // BBC World
  "https://moxie.foxnews.com/google-publisher/latest.xml",      // Fox News
  "https://www.cbsnews.com/latest/rss/main",                    // CBS News
  "https://feeds.abcnews.com/abcnews/topstories",               // ABC News
  "https://rssfeeds.usatoday.com/usatoday-NewsTopStories",      // USA Today
];

const TIMEOUT_MS = 8000; // 8 seconds per proxy attempt

// ── Parse XML ─────────────────────────────────────────────────────────────────
function parseTitles(xml) {
  if (!xml || xml.trim().length < 10) return [];
  try {
    const doc   = new DOMParser().parseFromString(xml, "text/xml");
    const items = [...doc.querySelectorAll("item title, entry title")];
    return items.map((el) => el.textContent.trim()).filter(Boolean);
  } catch {
    return [];
  }
}

// ── Fetch one feed, trying each proxy in sequence ────────────────────────────
async function fetchFeedWithFallback(feedUrl) {
  for (const proxy of PROXIES) {
    try {
      const controller = new AbortController();
      const timeout    = setTimeout(() => controller.abort(), TIMEOUT_MS);

      const res = await fetch(proxy.buildUrl(feedUrl), { signal: controller.signal });
      clearTimeout(timeout);

      if (!res.ok) continue;

      const xml    = await proxy.extractXml(res);
      const titles = parseTitles(xml);

      if (titles.length > 0) {
        return titles; // success — stop trying other proxies for this feed
      }
    } catch (err) {
      // Timeout / network / parse error — try next proxy
      console.warn(`[RSS] ${proxy.name} failed for ${feedUrl.split("/")[2]}:`, err?.message ?? err);
    }
  }
  return []; // all proxies exhausted for this feed
}

// ── Public API ────────────────────────────────────────────────────────────────
/**
 * Fetches headlines from all configured feeds concurrently.
 * Uses proxy fallback per-feed. Never throws.
 * @returns {Promise<string[]>} up to 30 headlines
 */
export async function fetchRSSHeadlines() {
  const results = await Promise.allSettled(FEEDS.map(fetchFeedWithFallback));

  let headlines = [];
  for (const result of results) {
    if (result.status === "fulfilled" && result.value.length > 0) {
      headlines = headlines.concat(result.value);
    }
  }

  if (headlines.length === 0) {
    console.warn("[RSS] All feeds failed across all proxies. Check network/CORS.");
  }

  return headlines.slice(0, 30);
}
